import { useMemo } from "react";
import { useTasksByItem } from "@coworker/apprestructured/src/task-management/services/useTasksByItem";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useTaskLinksByArticle } from "@coworker/app/src/components/RoomSettings/hooks/useTaskLinksByArticle";
import { useTasksByIds } from "@coworker/apprestructured/src/shared/hooks/tasks/useTasksByIds";
import { useItemsInfoQueries } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { MediaTaskListItem, MediaTasksData } from "../types";
import {
  UUID_V1_PATTERN,
  MEDIA_TASK_TYPE_NAMES,
} from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { mapItemsByNo } from "../utils";

/**
 * Hook to fetch tasks for an item across all medias (used in search results)
 * Combines v1 (task_links) and v2 (tasks-v2) data sources
 */
export function useMediaTasksDataForItem(
  storeId: string,
  itemNo: string
): MediaTasksData {
  // Get task types and filter for media maintenance types
  const { data: taskTypes = [] } = useAllTaskTypes();
  const mediaTaskTypeIds = useMemo(
    () =>
      taskTypes
        .filter((tt) =>
          (MEDIA_TASK_TYPE_NAMES as readonly string[]).includes(tt.name)
        )
        .map((tt) => tt.id),
    [taskTypes]
  );

  // Fetch V2 tasks from /tasks-v2/by-item (all medias)
  const {
    data: v2InfiniteData,
    isLoading: isLoadingV2,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useTasksByItem(itemNo, storeId, mediaTaskTypeIds);

  const v2Tasks = useMemo(
    () => v2InfiniteData?.pages?.flatMap((page) => page.tasks) || [],
    [v2InfiniteData]
  );

  const v2TotalCount = v2InfiniteData?.pages?.[0]?.totalCount || 0;

  // Fetch V1 tasks via task_links by article
  const { data: taskLinks = [] } = useTaskLinksByArticle(storeId, itemNo);

  const v1TaskIds = useMemo(
    () =>
      (taskLinks || [])
        .map((tl) => tl.taskId)
        .filter((id) => UUID_V1_PATTERN.test(id)),
    [taskLinks]
  );

  const { data: v1Tasks = [], isLoading: isLoadingV1 } = useTasksByIds(
    v1TaskIds,
    taskLinks || []
  );

  // Get item info for V1 tasks
  const itemNos = useMemo(() => {
    return v1Tasks
      .map((task) => task.product_article_id)
      .filter(Boolean) as string[];
  }, [v1Tasks]);

  const itemsInfoResult = useItemsInfoQueries(itemNos);
  const itemsInfo = useMemo(() => {
    if (itemNos.length === 0) return [];
    return itemsInfoResult
      .map((result) => result.data)
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
  }, [itemsInfoResult, itemNos]);

  const itemsMap = useMemo(() => mapItemsByNo(itemsInfo), [itemsInfo]);

  // Calculate combined total (use v1TaskIds.length for accurate count)
  const totalTaskCount =
    v2TotalCount > 0
      ? v2TotalCount + v1TaskIds.length
      : v2Tasks.length + v1Tasks.length;

  // Merge both arrays
  const allTasks: MediaTaskListItem[] = useMemo(
    () => [
      ...v1Tasks.map((task) => ({ type: "v1" as const, task })),
      ...v2Tasks.map((task) => ({ type: "v2" as const, task })),
    ],
    [v1Tasks, v2Tasks]
  );

  const isLoading = isLoadingV1 || isLoadingV2;

  return {
    allTasks,
    totalTaskCount,
    isLoading,
    itemsMap,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    v1TaskLinks: taskLinks || [],
    hasV2Tasks: v2TotalCount > 0,
    hasOngoingTasks: v1TaskIds.length > 0 || v2TotalCount > 0,
  };
}
